/**
 * useAnnotation 훅
 * Annotation 상태 관리 및 CRUD 기능 제공
 */

import { useState, useCallback } from 'react';
import {
  Annotation,
  AnnotationTool,
  AnnotationState,
  UnderlineStyle,
  HighlighterAnnotation,
  UnderlineAnnotation,
  ANNOTATION_DEFAULTS,
  HIGHLIGHTER_COLORS,
  UNDERLINE_COLORS,
} from '../types/Annotation';
import { generateAnnotationId } from '../utils/annotationUtils';

export interface UseAnnotationProps {
  /** 초기 annotation 배열 */
  initialAnnotations?: Annotation[];
  /** annotation 변경 시 콜백 (외부 상태 동기화용) */
  onAnnotationsChange?: (annotations: Annotation[]) => void;
}

export interface UseAnnotationReturn {
  /** 현재 annotation 상태 */
  state: AnnotationState;

  /** 도구 변경 */
  setTool: (tool: AnnotationTool) => void;

  /** 색상 변경 */
  setColor: (color: string) => void;

  /** 선 두께 변경 */
  setStrokeWidth: (width: number) => void;

  /** 투명도 변경 (형광펜용) */
  setOpacity: (opacity: number) => void;

  /** 밑줄 스타일 변경 */
  setUnderlineStyle: (style: UnderlineStyle) => void;

  /** 형광펜 annotation 추가 */
  addHighlighter: (
    points: { x: number; y: number }[],
    options?: Partial<Pick<HighlighterAnnotation, 'color' | 'strokeWidth' | 'opacity'>>
  ) => void;

  /** 밑줄 annotation 추가 */
  addUnderline: (
    startPoint: { x: number; y: number },
    endPoint: { x: number; y: number },
    options?: Partial<Pick<UnderlineAnnotation, 'color' | 'strokeWidth' | 'style'>>
  ) => void;

  /** annotation 삭제 */
  deleteAnnotation: (id: string) => void;

  /** 모든 annotation 삭제 */
  clearAnnotations: () => void;

  /** annotation 배열 직접 설정 (외부에서 주입 시) */
  setAnnotations: (annotations: Annotation[]) => void;
}

export function useAnnotation({
  initialAnnotations = [],
  onAnnotationsChange,
}: UseAnnotationProps = {}): UseAnnotationReturn {
  const [state, setState] = useState<AnnotationState>({
    annotations: initialAnnotations,
    currentTool: 'none',
    currentColor: HIGHLIGHTER_COLORS[0],
    currentStrokeWidth: ANNOTATION_DEFAULTS.highlighter.strokeWidth,
    currentOpacity: ANNOTATION_DEFAULTS.highlighter.opacity,
    currentUnderlineStyle: ANNOTATION_DEFAULTS.underline.style,
  });

  // 상태 업데이트 헬퍼 (콜백 호출 포함)
  const updateAnnotations = useCallback(
    (updater: (prev: Annotation[]) => Annotation[]) => {
      setState((prev) => {
        const newAnnotations = updater(prev.annotations);
        if (onAnnotationsChange) {
          onAnnotationsChange(newAnnotations);
        }
        return { ...prev, annotations: newAnnotations };
      });
    },
    [onAnnotationsChange]
  );

  // 도구 변경
  const setTool = useCallback((tool: AnnotationTool) => {
    setState((prev) => {
      // 도구 변경 시 해당 도구의 기본 색상과 두께로 설정
      let newColor = prev.currentColor;
      let newStrokeWidth = prev.currentStrokeWidth;

      if (tool === 'highlighter') {
        // 현재 색상이 형광펜 색상이 아니면 기본 형광펜 색상으로
        if (!HIGHLIGHTER_COLORS.includes(prev.currentColor as typeof HIGHLIGHTER_COLORS[number])) {
          newColor = HIGHLIGHTER_COLORS[0];
        }
        newStrokeWidth = ANNOTATION_DEFAULTS.highlighter.strokeWidth;
      } else if (tool === 'underline') {
        // 현재 색상이 밑줄 색상이 아니면 기본 밑줄 색상으로
        if (!UNDERLINE_COLORS.includes(prev.currentColor as typeof UNDERLINE_COLORS[number])) {
          newColor = UNDERLINE_COLORS[0];
        }
        newStrokeWidth = ANNOTATION_DEFAULTS.underline.strokeWidth;
      }

      return {
        ...prev,
        currentTool: tool,
        currentColor: newColor,
        currentStrokeWidth: newStrokeWidth,
      };
    });
  }, []);

  // 색상 변경
  const setColor = useCallback((color: string) => {
    setState((prev) => ({ ...prev, currentColor: color }));
  }, []);

  // 선 두께 변경
  const setStrokeWidth = useCallback((width: number) => {
    setState((prev) => ({ ...prev, currentStrokeWidth: width }));
  }, []);

  // 투명도 변경
  const setOpacity = useCallback((opacity: number) => {
    setState((prev) => ({
      ...prev,
      currentOpacity: Math.max(0, Math.min(1, opacity)),
    }));
  }, []);

  // 밑줄 스타일 변경
  const setUnderlineStyle = useCallback((style: UnderlineStyle) => {
    setState((prev) => ({ ...prev, currentUnderlineStyle: style }));
  }, []);

  // 형광펜 추가
  const addHighlighter = useCallback(
    (
      points: { x: number; y: number }[],
      options?: Partial<Pick<HighlighterAnnotation, 'color' | 'strokeWidth' | 'opacity'>>
    ) => {
      if (points.length < 2) return;

      const annotation: HighlighterAnnotation = {
        id: generateAnnotationId(),
        type: 'highlighter',
        points: points.map((p) => ({ x: p.x, y: p.y })),
        color: options?.color ?? state.currentColor,
        strokeWidth: options?.strokeWidth ?? state.currentStrokeWidth,
        opacity: options?.opacity ?? state.currentOpacity,
        createdAt: Date.now(),
      };

      updateAnnotations((prev) => [...prev, annotation]);
    },
    [state.currentColor, state.currentStrokeWidth, state.currentOpacity, updateAnnotations]
  );

  // 밑줄 추가
  const addUnderline = useCallback(
    (
      startPoint: { x: number; y: number },
      endPoint: { x: number; y: number },
      options?: Partial<Pick<UnderlineAnnotation, 'color' | 'strokeWidth' | 'style'>>
    ) => {
      const annotation: UnderlineAnnotation = {
        id: generateAnnotationId(),
        type: 'underline',
        startPoint: { x: startPoint.x, y: startPoint.y },
        endPoint: { x: endPoint.x, y: endPoint.y },
        color: options?.color ?? state.currentColor,
        strokeWidth: options?.strokeWidth ?? state.currentStrokeWidth,
        style: options?.style ?? state.currentUnderlineStyle,
        createdAt: Date.now(),
      };

      updateAnnotations((prev) => [...prev, annotation]);
    },
    [state.currentColor, state.currentStrokeWidth, state.currentUnderlineStyle, updateAnnotations]
  );

  // annotation 삭제
  const deleteAnnotation = useCallback(
    (id: string) => {
      updateAnnotations((prev) => prev.filter((a) => a.id !== id));
    },
    [updateAnnotations]
  );

  // 모든 annotation 삭제
  const clearAnnotations = useCallback(() => {
    updateAnnotations(() => []);
  }, [updateAnnotations]);

  // annotation 직접 설정 (외부에서 주입 시 - onAnnotationsChange 호출하지 않음)
  const setAnnotations = useCallback(
    (annotations: Annotation[]) => {
      setState((prev) => ({ ...prev, annotations }));
    },
    []
  );

  return {
    state,
    setTool,
    setColor,
    setStrokeWidth,
    setOpacity,
    setUnderlineStyle,
    addHighlighter,
    addUnderline,
    deleteAnnotation,
    clearAnnotations,
    setAnnotations,
  };
}
